import { useState } from "react";
import { motion } from "framer-motion";
import { Github, Twitter, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_spectral-design-1/artifacts/cufzcxy8_Futuristic%20rocket-shaped%20logo%20with%20glow%20no%20background.png";

const Footer = () => {
  const [clickCount, setClickCount] = useState(0);
  const navigate = useNavigate();

  const handleLogoClick = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);
    
    if (newCount >= 3) {
      setClickCount(0);
      navigate("/admin");
    }
    
    // Reset after 2 seconds if not enough clicks
    setTimeout(() => setClickCount(0), 2000);
  };

  const links = {
    product: [
      { label: "Features", href: "#features" },
      { label: "How It Works", href: "#how-it-works" },
      { label: "Pricing", href: "#pricing" },
      { label: "Security", href: "#security" },
    ],
    company: [
      { label: "About", href: "#about" },
      { label: "Blog", href: "#" },
      { label: "Careers", href: "#" },
      { label: "Contact", href: "#" },
    ],
    legal: [
      { label: "Privacy Policy", href: "#" },
      { label: "Terms of Service", href: "#" },
      { label: "Cookie Policy", href: "#" },
    ],
  };

  const socials = [
    { icon: Twitter, href: "#", label: "Twitter" },
    { icon: Github, href: "#", label: "GitHub" },
    { icon: Mail, href: "#", label: "Email" },
  ];

  return (
    <footer
      className="relative py-12 md:py-20 border-t border-white/10"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Main footer grid - Responsive */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 md:gap-12 mb-8 md:mb-12">
          {/* Brand - Full width on mobile */}
          <div className="col-span-2 md:col-span-2">
            <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-6">
              <button
                onClick={handleLogoClick}
                className="w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 rounded-xl overflow-hidden hover:shadow-[0_0_30px_rgba(0,212,255,0.6)] transition-all duration-300 cursor-pointer touch-manipulation"
                title={clickCount > 0 ? `${3 - clickCount} more clicks...` : ""}
              >
                <img 
                  src={LOGO_URL}
                  alt="GHOST"
                  className="w-full h-full object-contain"
                  style={{
                    filter: "drop-shadow(0 0 12px rgba(0,212,255,0.6))",
                  }}
                />
              </button>
              <span className="font-['Unbounded'] font-bold text-xl md:text-2xl lg:text-3xl">
                GHOST
              </span>
            </div>
            <p className="text-[#A1A1AA] text-sm md:text-base leading-relaxed mb-4 md:mb-6 max-w-sm">
              The AI-powered trading simulator for meme coins. Built by Future
              Gen AI — Indigenous-founded, Australian-built.
            </p>
            <div className="flex gap-3 md:gap-4">
              {socials.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full glass flex items-center justify-center ice-hover touch-manipulation active:scale-95"
                  aria-label={social.label}
                  data-testid={`footer-social-${social.label.toLowerCase()}`}
                >
                  <social.icon size={18} className="text-[#A1A1AA] md:w-[22px] md:h-[22px]" />
                </a>
              ))}
            </div>
          </div>

          {/* Links - 3 columns */}
          <div>
            <h4 className="font-semibold text-white text-base md:text-lg mb-3 md:mb-4">Product</h4>
            <ul className="space-y-2 md:space-y-3">
              {links.product.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-sm md:text-base text-[#A1A1AA] hover:text-white transition-colors touch-manipulation"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white text-base md:text-lg mb-3 md:mb-4">Company</h4>
            <ul className="space-y-2 md:space-y-3">
              {links.company.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-sm md:text-base text-[#A1A1AA] hover:text-white transition-colors touch-manipulation"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white text-base md:text-lg mb-3 md:mb-4">Legal</h4>
            <ul className="space-y-2 md:space-y-3">
              {links.legal.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-sm md:text-base text-[#A1A1AA] hover:text-white transition-colors touch-manipulation"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-6 md:pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4">
          <p className="text-xs md:text-base text-[#A1A1AA]">
            © 2025 Future Gen AI. All rights reserved.
          </p>
          <p className="text-xs md:text-base text-[#A1A1AA]">
            Made with 💜 in Western Australia
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
